import type { Dayjs } from 'dayjs'
import dayjsInstance from 'dayjs'
import relativeTimePlugin from 'dayjs/plugin/relativeTime.js'
import dayjsLocalizedFmtPlugin from 'dayjs/plugin/localizedFormat.js'
import isSameOrAfterPlugin from 'dayjs/plugin/isSameOrAfter.js'
import isSameOrBeforePlugin from 'dayjs/plugin/isSameOrBefore.js'

dayjsInstance.extend(relativeTimePlugin)
dayjsInstance.extend(dayjsLocalizedFmtPlugin)
dayjsInstance.extend(isSameOrAfterPlugin)
dayjsInstance.extend(isSameOrBeforePlugin)

export const dayjs = dayjsInstance

export const formatDayMonthBasedOnBrowserLang = (date: Date) => {
  const userLocale = navigator.language || navigator.languages[0] || 'en-GB'

  const isMonthFirst = userLocale.startsWith('en-US')
  return dayjs(date).format(isMonthFirst ? 'MM/DD' : 'DD/MM')
}

export const formatDateTimeAgo = (dateStr?: string | null) => {
  return dateStr ? dayjs().to(dayjs(dateStr)) : dateStr
}

export const formatDateTime = (dateStr?: string | null, format = 'lll') => {
  // https://day.js.org/docs/en/display/format#list-of-localized-formats
  return dateStr ? dayjs(dateStr).format(format) : dateStr
}

export const formatDateDetailed = (dateValue?: string | null, isDetailedEnabled?: boolean) => {
  if (!dateValue) return dateValue

  if (isDetailedEnabled === true) {
    const date = dayjs(dateValue)
    const dateFormatted = date.format('LL') // "Month Day, Year"
    const timeFormatted = date.format('LTS') // "h:mm:ss PM" - explicit format with seconds
    return `${dateFormatted}<br>${timeFormatted}`
  }
  return formatDate(dateValue, 'LL')
}

export const formatDate = (dateStr?: string | null | Dayjs, format = 'L') => {
  // https://day.js.org/docs/en/display/format#list-of-localized-formats
  return dateStr ? dayjs(dateStr).format(format) : dateStr
}

export const isBefore = (dateStr?: string | null): boolean => {
  // https://day.js.org/docs/en/query/is-before
  return !!(dateStr ? dayjs().isBefore(dayjs(dateStr)) : dateStr)
}

export const isSameOrBefore = (dateStr?: string | null): boolean => {
  // https://day.js.org/docs/en/query/is-same-or-before
  return !!(dateStr ? dayjs().isSameOrBefore(dayjs(dateStr)) : dateStr)
}

export const isAfter = (dateStr?: string | null): boolean => {
  // https://day.js.org/docs/en/query/is-after
  return !!(dateStr ? dayjs().isAfter(dayjs(dateStr)) : dateStr)
}

export const isSameOrAfter = (dateStr?: string | null): boolean => {
  // https://day.js.org/docs/en/query/is-same-or-after
  return !!(dateStr ? dayjs().isSameOrAfter(dayjs(dateStr)) : dateStr)
}

export const msToSeconds = (ms: number) => Math.round(ms / 1000)

export const secondsToHumanReadable = (seconds: number) => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  return [hours, minutes, secs].map((v) => (v < 10 ? '0' + v : v)).join(':')
}
